export type CategoryId = "timeline" | "clue" | "mystery" | "profile";

export interface CategoryMeta {
  id: CategoryId;
  name: string;
  en: string;
  desc: string;
  chip: string;
  dot: string;
}

export const categories: CategoryMeta[] = [
  {
    id: "timeline",
    name: "时间线复盘",
    en: "TIMELINE",
    desc: "把散落的热搜节点串成一条清晰的线，还原事件如何一步步走到今天。",
    chip: "bg-accent-100 text-accent-900",
    dot: "bg-accent-500",
  },
  {
    id: "clue",
    name: "隐藏线索",
    en: "CLUES",
    desc: "逐帧看文字、图片与发言措辞，找出容易被一眼带过的细节。",
    chip: "bg-primary-100 text-primary-900",
    dot: "bg-primary-500",
  },
  {
    id: "mystery",
    name: "未解之谜",
    en: "MYSTERIES",
    desc: "那些讨论区吵翻却仍没有答案的疑点，我们帮你逐条归类存档。",
    chip: "bg-secondary-200 text-secondary-900",
    dot: "bg-secondary-500",
  },
  {
    id: "profile",
    name: "人物关联",
    en: "PEOPLE",
    desc: "理清事件中每个人的角色、时机与彼此的交集，避免只看单一视角。",
    chip: "bg-foreground-200 text-foreground-800",
    dot: "bg-foreground-500",
  },
];

export function getCategory(id: CategoryId): CategoryMeta {
  return categories.find((c) => c.id === id) ?? categories[0];
}

export interface Article {
  id: string;
  slug: string;
  category: CategoryId;
  kicker: string;
  title: string;
  summary: string;
  author: string;
  date: string;
  readMin: number;
  views: number;
  status: string;
  featured?: boolean;
  cover: string;
  wideCover?: string;
  tags: string[];
  paragraphs: { heading?: string; body: string[] }[];
}

export const articles: Article[] = [
  {
    id: "a1",
    slug: "publish-time-mismatch",
    category: "clue",
    kicker: "隐藏线索 · 逐帧看细节",
    title: "发布时间与配图的错位，往往是第一个被忽略的疑点",
    summary:
      "一张凌晨发出的配图，评论区却早已在几小时前讨论过内容本身。时间戳与画面之间那点「对不上」，常常是整个故事真正的开头。",
    author: "瓜田编辑组",
    date: "09-08",
    readMin: 7,
    views: 128000,
    status: "疑点",
    featured: true,
    cover:
      "https://readdy.ai/api/search-image?query=editorial%20flat%20illustration%20of%20a%20cluttered%20night%20desk%20with%20laptop%2C%20clocks%20and%20folded%20newspapers%2C%20warm%20cream%20paper%20background%2C%20ink%20outline%2C%20watermelon%20red%20and%20melon%20green%20accents%2C%20grainy%20print%20texture%2C%20no%20text&width=800&height=560&seq=cover-clue-01&orientation=landscape",
    wideCover:
      "https://readdy.ai/api/search-image?query=wide%20editorial%20magazine%20illustration%20of%20a%20detective%20style%20desk%20board%20with%20pinned%20notes%2C%20threads%20and%20clock%20faces%2C%20warm%20cream%20background%2C%20ink%20lines%2C%20watermelon%20red%20thread%2C%20mel%20on%20green%20accents%2C%20no%20text&width=1200&height=700&seq=wide-clue-01&orientation=landscape",
    tags: ["发布时间", "配图对比", "细节盘点"],
    paragraphs: [
      {
        heading: "先对时间，再谈内容",
        body: [
          "复盘一个热点，最容易被跳过的一步其实是「对表」。发文时间、配图水印时间、评论区的首条时间戳，三者排在一起，很多看似顺理成章的逻辑会立刻露出松动。",
          "昨天这起事件的讨论里，最热闹的转评都集中在前一天深夜，而号称「首发」的那条配图微博，时间却落在更晚的清晨。发布时间早于内容被讨论的时间，本身就是需要解释的异常。",
        ],
      },
      {
        heading: "错位意味着什么",
        body: [
          "我们无意替任何一方定性，只把疑问摆出来：如果配图确实很早就存在，为什么现在才发；如果内容是临时拼贴，那第一版又删掉了什么。这两种可能指向完全不同的叙事。",
          "把这类「对不上」记录下来，是为了提醒自己：在情绪化的评论区里，客观可核对的时间，反而最能帮我们保持清醒。",
        ],
      },
    ],
  },
  {
    id: "a2",
    slug: "unanswered-call",
    category: "mystery",
    kicker: "未解之谜 · 悬着的那通电话",
    title: "那通「未接来电」之后，故事的走向开始变得奇怪",
    summary:
      "事件中反复被提及却始终没有下文的一通电话，成了讨论区公认的最大悬案。当事人前后的措辞变化，让这通电话的意义更加模糊。",
    author: "瓜田编辑组",
    date: "09-07",
    readMin: 6,
    views: 96000,
    status: "悬案",
    featured: true,
    cover:
      "https://readdy.ai/api/search-image?query=editorial%20illustration%20of%20an%20old%20rotary%20phone%20on%20a%20warm%20cream%20paper%20table%2C%20ink%20outline%2C%20watermelon%20red%20cord%2C%20melon%20green%20accents%2C%20minimal%20flat%2C%20grainy%20texture%2C%20no%20text&width=800&height=560&seq=cover-mystery-02&orientation=landscape",
    wideCover:
      "https://readdy.ai/api/search-image?query=wide%20editorial%20illustration%20of%20a%20dark%20room%20with%20a%20single%20ringing%20phone%20light%2C%20shadow%20silhouette%2C%20warm%20cream%20and%20ink%2C%20melon%20green%20tint%2C%20watermelon%20red%20accent%2C%20flat%20graphic%2C%20no%20text&width=1200&height=700&seq=wide-mystery-02&orientation=landscape",
    tags: ["一通电话", "措辞对比", "悬案档案"],
    paragraphs: [
      {
        heading: "被反复提起的电话",
        body: [
          "几乎所有时间线都会提到那通「未接来电」，但没有任何一方给出过完整的解释。它像一根线头，露在外面，却没人把它拉直。",
          "我们统计了昨天讨论里提到这通电话的高赞内容，大多数网友关心的其实是同一个问题：为什么它在关键时刻恰好没人接。",
        ],
      },
      {
        heading: "为什么仍然存疑",
        body: [
          "问题在于，此后几方对这段通联的描述存在细微出入——接通与否、时长长短、谁先挂断，几次说法都不完全一致。",
          "在没有录音或通话记录这类硬证据之前，任何「还原」都只是基于措辞的推测。这正是我们把这件事归入未解之谜、而不是直接下结论的原因。",
        ],
      },
    ],
  },
  {
    id: "a3",
    slug: "old-friends-in-the-story",
    category: "profile",
    kicker: "人物关联 · 一张关系网",
    title: "盘点事件中被反复点到名的几位「老朋友」",
    summary:
      "比起主角本人，那些出现在声明、评论区与旧合照里的熟人，往往透露更多信息。我们把他们的出现时机排出来看，才发现有意思的重合。",
    author: "瓜田编辑组",
    date: "09-06",
    readMin: 8,
    views: 74000,
    status: "梳理",
    cover:
      "https://readdy.ai/api/search-image?query=editorial%20illustration%20of%20a%20network%20of%20connected%20portrait%20circles%20linked%20by%20lines%20on%20warm%20cream%20paper%2C%20flat%20ink%2C%20watermelon%20red%20and%20melon%20green%20nodes%2C%20no%20text&width=800&height=560&seq=cover-people-03&orientation=landscape",
    tags: ["关系图谱", "熟人", "时机"],
    paragraphs: [
      {
        heading: "配角们的出场顺序",
        body: [
          "把几位「被点到名」的朋友，按首次出现在公开讨论里的时间排序，会发现一个共同点：他们几乎都在事件最胶着的那一两天集中发声。",
          "巧合当然存在，但当三四个人的发言时间窗口高度重合，就值得把这几天当成一条独立的时间线来整理。",
        ],
      },
      {
        heading: "我们如何呈现",
        body: [
          "这里我们只汇总已公开的信息与各自说法，不替任何一方站队。人物之间的真实关系、私下是否有交集，都不在可核实范围内。",
          "把出现时机画成图，是为了让读者一眼看清节奏，而不是暗示谁在「带节奏」。这也是「人物关联」栏目一贯的边界。",
        ],
      },
    ],
  },
  {
    id: "a4",
    slug: "forty-eight-hour-timeline",
    category: "timeline",
    kicker: "时间线复盘 · 全程整理",
    title: "48 小时完整时间线：从热搜发酵到本人回应",
    summary:
      "从第一次小范围讨论，到冲上热搜，再到正式回应，我们用一条可核对的时间轴，把昨天这 48 小时里发生的一切重新排演了一遍。",
    author: "瓜田编辑组",
    date: "09-05",
    readMin: 9,
    views: 153000,
    status: "复盘",
    featured: true,
    cover:
      "https://readdy.ai/api/search-image?query=editorial%20illustration%20of%20a%20long%20horizontal%20timeline%20with%20dots%20and%20spiral%20paper%2C%20warm%20cream%20background%2C%20flat%20ink%2C%20melon%20green%20line%2C%20watermelon%20red%20milestone%20dots%2C%20no%20text&width=800&height=560&seq=cover-timeline-04&orientation=landscape",
    wideCover:
      "https://readdy.ai/api/search-image?query=wide%20editorial%20timeline%20chart%20illustration%20with%20milestone%20nodes%20and%20connecting%20curve%20on%20cream%20paper%2C%20ink%20lines%2C%20watermelon%20red%20and%20melon%20green%2C%20flat%20minimal%2C%20no%20text&width=1200&height=700&seq=wide-timeline-04&orientation=landscape",
    tags: ["时间线", "热搜", "当事人回应"],
    paragraphs: [
      {
        heading: "从角落到首页",
        body: [
          "一切开始于某个小圈层的只言片语。真正让事件进入大众视野的，是某条被大量转发的截图。我们把它出现的准确节点标记为时间轴的起点。",
          "随后的十几个小时里，讨论在多个平台间来回跳转，信息源也从单一账号扩散成多种声音。",
        ],
      },
      {
        heading: "回应之后",
        body: [
          "当事人发布回应后，热度不降反升——因为回应里留下的「留白」，成了下一轮讨论的燃料。我们把回应的关键措辞做了逐句标注。",
          "到第 48 小时，事件仍在发酵。时间线的价值不在于宣告「结束」，而在于让后来者不必再从零拼图。",
        ],
      },
    ],
  },
  {
    id: "a5",
    slug: "wording-shift-in-blessing",
    category: "clue",
    kicker: "隐藏线索 · 措辞显微镜",
    title: "一句「祝福」背后的措辞变化，藏着多少信息量",
    summary:
      "从「希望」到「相信」，从「你们」到「你」，短短一句话里人称与时态的变化，往往比内容本身更值得逐字细读。",
    author: "观察员 阿瓜",
    date: "09-04",
    readMin: 5,
    views: 52000,
    status: "疑点",
    cover:
      "https://readdy.ai/api/search-image?query=editorial%20illustration%20of%20a%20magnifying%20glass%20over%20lines%20of%20text%20on%20cream%20paper%2C%20flat%20ink%2C%20watermelon%20red%20highlighter%20marks%2C%20melon%20green%20accent%2C%20no%20text&width=800&height=560&seq=cover-clue-05&orientation=landscape",
    tags: ["措辞", "人称", "逐字分析"],
    paragraphs: [
      {
        body: [
          "公关措辞是少数可以反复回看的文本。我们把这两天几份公开声明的关键词挑出来做了一次并排，发现人称和情态动词的变化最有信息量。",
          "从泛指到特指、从展望到确认，每一次用词收紧，通常都意味着态度在向某个明确方向移动。",
        ],
      },
      {
        heading: "看词，也看删掉的部分",
        body: [
          "同样值得注意的，是那些被删掉又重发的版本。原始版本里多出来的一句，往往比最终保留的内容更接近真实状态。",
          "需要说明的是，措辞分析属于文本层面的观察，并不等于事实判断——这也是我们把分析限定在「线索」而非「结论」的原因。",
        ],
      },
    ],
  },
  {
    id: "a6",
    slug: "the-seed-six-months-ago",
    category: "timeline",
    kicker: "时间线复盘 · 前情",
    title: "前情回顾：这瓜的线，早在半年前就已经埋下",
    summary:
      "很多人以为是昨天才发生的事，其实半年前就出现过零星信号。我们把「旧事」翻出来，才发现今天的热搜有迹可循。",
    author: "瓜田编辑组",
    date: "09-03",
    readMin: 7,
    views: 61000,
    status: "复盘",
    cover:
      "https://readdy.ai/api/search-image?query=editorial%20illustration%20of%20seeds%20growing%20into%20a%20vine%20across%20months%20on%20cream%20paper%2C%20flat%20ink%2C%20melon%20green%20leaves%2C%20watermelon%20red%20fruit%2C%20timeline%20dots%2C%20no%20text&width=800&height=560&seq=cover-timeline-06&orientation=landscape",
    tags: ["前情", "伏笔", "考古"],
    paragraphs: [
      {
        body: [
          "把时间轴拉长到半年，今天的热搜就有了清晰的上下文。当时的互动、一次仓促的澄清、一句被忽略的话，都可能是今天的导火索。",
          "做前情回顾不是为了「事后诸葛亮」，而是想说明：大多数看起来突然的瓜，背后都有一条可追溯的线。",
        ],
      },
    ],
  },
  {
    id: "a7",
    slug: "second-scene-unsolved",
    category: "mystery",
    kicker: "未解之谜 · 第二现场",
    title: "至今没人解释清楚的「第二现场」，到底在哪",
    summary:
      "时间线里有一段「空窗期」，地点与在场者都无法被公开信息完全覆盖。这段空窗，成了所有推测最大的分岔口。",
    author: "观察员 阿瓜",
    date: "09-02",
    readMin: 6,
    views: 43000,
    status: "悬案",
    cover:
      "https://readdy.ai/api/search-image?query=editorial%20illustration%20of%20an%20empty%20corner%20room%20with%20a%20single%20door%20and%20question%20mark%20shadow%2C%20cream%20paper%2C%20flat%20ink%2C%20melon%20green%2C%20watermelon%20red%20accent%2C%20no%20text&width=800&height=560&seq=cover-mystery-07&orientation=landscape",
    tags: ["空窗期", "地点", "悬案档案"],
    paragraphs: [
      {
        body: [
          "公开信息能拼出绝大部分行程，唯独中间那几小时是空白的。没有图片、没有定位、没有任何可交叉验证的记录。",
          "这段空白本身不是证据，却是所有叙事分歧的起点。我们把它单独拎出来存档，而不是替读者脑补答案。",
        ],
      },
    ],
  },
  {
    id: "a8",
    slug: "agent-post-words",
    category: "profile",
    kicker: "人物关联 · 代言的语气",
    title: "经纪人那条深夜微博的用词，藏着多少信息量",
    summary:
      "深夜代发声、用词克制、句末没有标点——这几个细节叠加在一起，常被解读为某种紧急沟通后的产物。我们把它放进时间线里重新读一遍。",
    author: "瓜田编辑组",
    date: "09-01",
    readMin: 5,
    views: 38000,
    status: "梳理",
    cover:
      "https://readdy.ai/api/search-image?query=editorial%20illustration%20of%20a%20person%20typing%20on%20a%20phone%20at%20night%20under%20a%20lamp%2C%20cream%20paper%2C%20flat%20ink%2C%20watermelon%20red%20screen%20glow%2C%20melon%20green%20accent%2C%20no%20text&width=800&height=560&seq=cover-people-08&orientation=landscape",
    tags: ["代发声", "深夜", "语气"],
    paragraphs: [
      {
        body: [
          "谁发声、几点发声、用第一人称还是第三人称，这些「谁在说」的信息，往往比「说了什么」更能反映事件当下处于哪个阶段。",
          "深夜由团队账号发布、且通篇保持克制措辞，通常被业内视为「紧急沟通后的统一口径」。这只是一种观察，不代表我们知道对话内容。",
        ],
      },
    ],
  },
  {
    id: "a9",
    slug: "same-backdrop-comparison",
    category: "clue",
    kicker: "隐藏线索 · 画面比对",
    title: "两张「同款背景板」的细节比对：光影与物件对不上",
    summary:
      "两张声称拍摄于不同时间、不同地点的图，背景里的时钟、光线角度与陈设却出现微妙重合。我们把它们并排放好，让读者自己判断。",
    author: "图鉴员 阿芒",
    date: "08-31",
    readMin: 6,
    views: 49000,
    status: "疑点",
    cover:
      "https://readdy.ai/api/search-image?query=editorial%20illustration%20of%20two%20side%20by%20side%20photo%20frames%20showing%20similar%20rooms%20being%20compared%2C%20cream%20paper%2C%20flat%20ink%2C%20watermelon%20red%20comparison%20arrows%2C%20melon%20green%2C%20no%20text&width=800&height=560&seq=cover-clue-09&orientation=landscape",
    tags: ["图片比对", "光影", "陈设"],
    paragraphs: [
      {
        body: [
          "图片信息是少数能「直接目测」的证据。我们把流传最广的两张图放大后逐项对比，时钟刻度、光源方向与背景陈设都存在重合度较高的区域。",
          "需要诚实说明：这只能证明「同场景」，不能自动推出「同时间」。毕竟部分艺人确实会在固定空间多次拍摄。",
        ],
      },
    ],
  },
  {
    id: "a10",
    slug: "high-vote-doubts",
    category: "mystery",
    kicker: "未解之谜 · 网友集思",
    title: "讨论区最高赞的 5 个疑点，我们逐条做了整理",
    summary:
      "与其各自脑补，不如把讨论区被顶上来的疑点收集起来，逐条标注目前的公开信息能回答到什么程度。",
    author: "瓜田编辑组",
    date: "08-30",
    readMin: 8,
    views: 57000,
    status: "档案",
    cover:
      "https://readdy.ai/api/search-image?query=editorial%20illustration%20of%20a%20wall%20covered%20with%20sticky%20notes%20and%20questions%20on%20cream%20paper%2C%20flat%20ink%2C%20melon%20green%20and%20watermelon%20red%20notes%2C%20no%20text&width=800&height=560&seq=cover-mystery-10&orientation=landscape",
    tags: ["网友疑点", "整理", "集思"],
    paragraphs: [
      {
        body: [
          "我们把评论区与多个平台讨论里被反复顶起的疑点收集成五类，每一类都标注了「公开信息能否回答」。",
          "能回答的，我们给出可核对来源；暂时不能回答的，就诚实归入待解。整理本身不产出结论，只是让提问变得更有效。",
        ],
      },
    ],
  },
  {
    id: "a11",
    slug: "rhythm-before-statement",
    category: "timeline",
    kicker: "时间线复盘 · 节奏",
    title: "官方回应之前的「静默期」，信息流反而最值得看",
    summary:
      "真正关键的信息往往出现在当事人沉默的那几小时里——平台的删帖、账号的改动、关联方的小动作，都是可以观察的信号。",
    author: "观察员 阿瓜",
    date: "08-29",
    readMin: 7,
    views: 44000,
    status: "复盘",
    cover:
      "https://readdy.ai/api/search-image?query=editorial%20illustration%20of%20a%20quiet%20still%20clock%20and%20pause%20symbol%20on%20cream%20paper%2C%20flat%20ink%2C%20melon%20green%20calm%2C%20watermelon%20red%20small%20markers%2C%20no%20text&width=800&height=560&seq=cover-timeline-11&orientation=landscape",
    tags: ["静默期", "节奏", "信号"],
    paragraphs: [
      {
        body: [
          "静默不等于真空。我们在那几小时里观察到若干可记录的信号：某些内容的消失、账号主页的临时改动、关联账号的密集上线。",
          "这些信号都没有被当事人确认，因此我们只作为「观察记录」呈现，留给读者在回应出来后自行对照。",
        ],
      },
    ],
  },
  {
    id: "a12",
    slug: "well-timed-sightings",
    category: "profile",
    kicker: "人物关联 · 恰到好处",
    title: "那些「恰到好处」的偶遇，为什么总是出现在关键时刻",
    summary:
      "每到大瓜爆发，总有「路人偶遇」准时出现。它们的拍摄角度、清晰度与发布时间都异常合理，合理到让人想问一句：也太巧了。",
    author: "图鉴员 阿芒",
    date: "08-28",
    readMin: 5,
    views: 33000,
    status: "观察",
    cover:
      "https://readdy.ai/api/search-image?query=editorial%20illustration%20of%20a%20person%20with%20a%20phone%20taking%20a%20sneaky%20photo%20behind%20a%20plant%20on%20cream%20paper%2C%20flat%20ink%2C%20watermelon%20red%20and%20melon%20green%2C%20no%20text&width=800&height=560&seq=cover-people-12&orientation=landscape",
    tags: ["偶遇", "路人视角", "时机"],
    paragraphs: [
      {
        body: [
          "我们并不认为每次偶遇都是安排好的——真实偶遇一定存在。但当它出现的频率与事件热度几乎完全同步时，就值得多留一个心眼。",
          "判断一张偶遇图的真假，通常要看是否能找到无利可图的第三方佐证。找不到时，就把它归入「待观察」，而不是当成定论使用。",
        ],
      },
    ],
  },
];

export interface TimelineEvent {
  time: string;
  tag: string;
  title: string;
  desc: string;
  tone: "accent" | "primary" | "secondary";
}

export const timelineEvents: TimelineEvent[] = [
  {
    time: "第 0 小时",
    tag: "起点",
    title: "某账号抛出首张截图",
    desc: "一张来源不明的截图开始在小范围流传，措辞含混，尚未引起注意。",
    tone: "secondary",
  },
  {
    time: "第 6 小时",
    tag: "发酵",
    title: "讨论跳上公开平台",
    desc: "截图被搬运到多个平台，评论方向逐渐走向情绪化，信息开始变形。",
    tone: "accent",
  },
  {
    time: "第 14 小时",
    tag: "高热",
    title: "冲上热搜榜单前列",
    desc: "标题被反复改写，几个「次生话题」被拆分出来，真假信息混杂。",
    tone: "primary",
  },
  {
    time: "第 26 小时",
    tag: "转折",
    title: "涉事方先后发声",
    desc: "先有团队账号深夜回应，随后本人转发补充，措辞留有明显留白。",
    tone: "accent",
  },
  {
    time: "第 40 小时",
    tag: "争论",
    title: "质疑声转向「未解之谜」",
    desc: "回应并未平息讨论，反而带出新的疑点，多条旧闻被重新考古。",
    tone: "primary",
  },
  {
    time: "第 48 小时",
    tag: "待续",
    title: "事件仍在发酵中",
    desc: "截至复盘时点，尚无进一步说明。我们只整理已公开节点，不做预判。",
    tone: "secondary",
  },
];

export interface FaqItem {
  q: string;
  a: string;
}

export const faqs: FaqItem[] = [
  {
    q: "「昨日吃瓜」到底在做什么？",
    a: "我们把前一天网络上引发热议的事件，重新做一次有章法的复盘：整理时间线、比对图片与措辞、归档仍无答案的疑点。我们做的是「信息整理」，不是替任何一方下结论。",
  },
  {
    q: "你们的内容会点名真人、替别人定罪吗？",
    a: "不会。栏目只基于已公开的信息做时间、文字、画面层面的梳理，涉及无法核实的人与事一律标注「待证」，并在文末声明以官方说法为准，绝不用推测给真实的人贴标签。",
  },
  {
    q: "「吃瓜」会不会变成传播谣言？",
    a: "这正是我们最在意的地方。每条内容都遵循同一条原则：可核对的给来源，不可核对的只提问。来源不明的截图会明确标注，绝不当作既定事实转发。",
  },
  {
    q: "更新频率和栏目如何划分？",
    a: "我们按事件复盘，通常在热点发酵后 24 到 48 小时内更新。内容分成「时间线复盘」「隐藏线索」「未解之谜」「人物关联」四个栏目，方便按兴趣取用。",
  },
  {
    q: "你们的信息来源可靠吗？",
    a: "所有时间节点、公开声明、可检索截图都来自各平台可复核的公开内容。我们在文中会尽量标明「哪条信息来自谁、什么时间发布」，把核实的主动权交还给读者。",
  },
  {
    q: "发现我整理的信息有误，如何反馈？",
    a: "欢迎通过页面底部的联系方式告诉我们。信息整理难免有遗漏，收到纠错后我们会第一时间核对，并在确认无误后修正或补充标注。",
  },
];

export interface BrandPrinciple {
  icon: string;
  title: string;
  desc: string;
}

export const principles: BrandPrinciple[] = [
  {
    icon: "ri-time-line",
    title: "先对表，再吃瓜",
    desc: "一切讨论从可核对的时间线开始。没有时间锚点的信息，我们不会当作事实使用。",
  },
  {
    icon: "ri-focus-2-line",
    title: "只提问，不定性",
    desc: "我们对无法核实的人与事，只把疑点摆出来归档，绝不用推测给真实的人贴标签。",
  },
  {
    icon: "ri-search-eye-line",
    title: "给来源，讲边界",
    desc: "能核对的给出来源，不能核对的明确标注「待证」，把判断权完整交还给读者。",
  },
  {
    icon: "ri-chat-check-line",
    title: "有理有据地热闹",
    desc: "吃瓜不必上头。我们希望每个话题都能退后一步，看节奏、看措辞、看证据，再开口。",
  },
];

export interface Testimonial {
  quote: string;
  author: string;
  role: string;
  color: string;
}

export const testimonials: Testimonial[] = [
  {
    quote: "第一次看到有人能把一场热闹梳理得这么清楚，时间线一摆，脑子立刻不乱了。",
    author: "深夜复盘党",
    role: "常读读者",
    color: "bg-primary-100 text-primary-900",
  },
  {
    quote: "最喜欢「措辞显微镜」那一类内容，看完才意识到自己平时错过多少细节。",
    author: "细节控",
    role: "订阅用户",
    color: "bg-accent-100 text-accent-900",
  },
  {
    quote: "难得有不带节奏的瓜田。质疑都标了「待证」，这点让人愿意留下来。",
    author: "理性围观者",
    role: "老读者",
    color: "bg-secondary-200 text-secondary-900",
  },
];